/**
 * GLAZ POL - Scroll Reveal & UX Animations
 * Wersja: 1.0.0
 *
 * Funkcjonalności:
 * - Scroll reveal dla sekcji
 * - Sticky header shrink
 * - Animowane liczniki
 * - Smooth scroll dla anchor linków
 */

(function() {
  'use strict';

  // Sprawdź czy użytkownik preferuje mniej ruchu
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // =====================================================
  // SCROLL REVEAL - Intersection Observer
  // =====================================================

  function initScrollReveal() {
    if (prefersReducedMotion) return;

    // Selektory sekcji do animowania
    const sectionSelectors = [
      '.trust-badges',
      '.glazpol-process',
      '.glazpol-faq',
      '.glazpol-testimonials',
      '.glazpol-categories',
      '.glazpol-newsletter',
      '.glazpol-blog',
      '.featured-collection',
      '.collection-list',
      '.product-recommendations',
      '[class*="glazpol-"]',
      '.shopify-section'
    ];

    // Elementy z klasą .reveal
    const revealElements = document.querySelectorAll('.reveal, .reveal-left, .reveal-right, .reveal-scale');

    // Sekcje do animowania
    const sections = document.querySelectorAll(sectionSelectors.join(', '));

    const observerOptions = {
      root: null,
      rootMargin: '0px 0px -100px 0px',
      threshold: 0.1
    };

    // Observer dla elementów z klasą reveal
    const elementObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('revealed');
          elementObserver.unobserve(entry.target);
        }
      });
    }, observerOptions);

    revealElements.forEach(el => elementObserver.observe(el));

    // Observer dla sekcji (automatyczny reveal dzieci)
    const sectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('section-revealed');
          sectionObserver.unobserve(entry.target);

          // Uruchom liczniki w tej sekcji
          initCountersInSection(entry.target);
        }
      });
    }, {
      root: null,
      rootMargin: '0px 0px -50px 0px',
      threshold: 0.15
    });

    sections.forEach(section => {
      // Nie animuj hero (już ma własne animacje)
      if (!section.classList.contains('glazpol-hero') &&
          !section.querySelector('.glazpol-hero')) {
        sectionObserver.observe(section);
      }
    });
  }

  // =====================================================
  // STICKY HEADER SHRINK
  // =====================================================

  function initStickyHeader() {
    const header = document.querySelector('header-component, .header, #header-component');
    if (!header) return;

    let lastScrollY = 0;
    let ticking = false;

    function updateHeader() {
      const scrollY = window.scrollY;

      if (scrollY > 100) {
        header.classList.add('header--scrolled');
      } else {
        header.classList.remove('header--scrolled');
      }

      lastScrollY = scrollY;
      ticking = false;
    }

    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(updateHeader);
        ticking = true;
      }
    }, { passive: true });
  }

  // =====================================================
  // ANIMOWANE LICZNIKI
  // =====================================================

  function initCountersInSection(section) {
    const counters = section.querySelectorAll('[data-counter], .counter-value, .stat-number');

    counters.forEach(counter => {
      if (counter.dataset.counted) return;
      counter.dataset.counted = 'true';

      const target = parseInt(counter.dataset.counter || counter.textContent.replace(/\D/g, ''), 10);
      const suffix = counter.dataset.suffix || '';
      const prefix = counter.dataset.prefix || '';
      const duration = parseInt(counter.dataset.duration, 10) || 2000;

      if (isNaN(target)) return;

      animateCounter(counter, 0, target, duration, prefix, suffix);
    });
  }

  function animateCounter(element, start, end, duration, prefix, suffix) {
    if (prefersReducedMotion) {
      element.textContent = prefix + end.toLocaleString('pl-PL') + suffix;
      return;
    }

    const startTime = performance.now();
    const range = end - start;

    function update(currentTime) {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function (ease-out)
      const easeOut = 1 - Math.pow(1 - progress, 3);

      const current = Math.floor(start + (range * easeOut));
      element.textContent = prefix + current.toLocaleString('pl-PL') + suffix;

      if (progress < 1) {
        requestAnimationFrame(update);
      } else {
        element.textContent = prefix + end.toLocaleString('pl-PL') + suffix;
      }
    }

    requestAnimationFrame(update);
  }

  // =====================================================
  // SMOOTH SCROLL DLA ANCHOR LINKÓW
  // =====================================================

  function initSmoothScroll() {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function(e) {
        const targetId = this.getAttribute('href');
        if (targetId === '#') return;

        const target = document.querySelector(targetId);
        if (!target) return;

        e.preventDefault();

        const headerHeight = document.querySelector('header-component, .header')?.offsetHeight || 0;
        const targetPosition = target.getBoundingClientRect().top + window.scrollY - headerHeight - 20;

        window.scrollTo({
          top: targetPosition,
          behavior: prefersReducedMotion ? 'auto' : 'smooth'
        });
      });
    });
  }

  // =====================================================
  // SCROLL PROGRESS INDICATOR (opcjonalne)
  // =====================================================

  function initScrollProgress() {
    // Utwórz element jeśli nie istnieje
    let progressBar = document.querySelector('.scroll-indicator');

    if (!progressBar) {
      progressBar = document.createElement('div');
      progressBar.className = 'scroll-indicator';
      document.body.prepend(progressBar);
    }

    function updateProgress() {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (scrollTop / docHeight) * 100;
      progressBar.style.width = progress + '%';
    }

    window.addEventListener('scroll', () => {
      requestAnimationFrame(updateProgress);
    }, { passive: true });
  }

  // =====================================================
  // PARALLAX ENHANCEMENT (już jest w hero, to tylko helper)
  // =====================================================

  function initParallaxElements() {
    if (prefersReducedMotion) return;

    const parallaxElements = document.querySelectorAll('[data-parallax]');

    if (parallaxElements.length === 0) return;

    function updateParallax() {
      const scrollY = window.scrollY;

      parallaxElements.forEach(el => {
        const speed = parseFloat(el.dataset.parallax) || 0.5;
        const rect = el.getBoundingClientRect();

        if (rect.bottom > 0 && rect.top < window.innerHeight) {
          const yPos = (scrollY - el.offsetTop) * speed;
          el.style.transform = `translateY(${yPos}px)`;
        }
      });
    }

    window.addEventListener('scroll', () => {
      requestAnimationFrame(updateParallax);
    }, { passive: true });
  }

  // =====================================================
  // LAZY LOAD ENHANCEMENT
  // =====================================================

  function enhanceLazyLoad() {
    // Dodaj fade-in dla lazy-loaded obrazków
    document.querySelectorAll('img[loading="lazy"]').forEach(img => {
      if (!img.complete) {
        img.style.opacity = '0';
        img.style.transition = 'opacity 0.5s ease';

        img.addEventListener('load', () => {
          img.style.opacity = '1';
        }, { once: true });
      }
    });
  }

  // =====================================================
  // INICJALIZACJA
  // =====================================================

  function init() {
    // Oznacz stronę jako załadowaną
    document.body.classList.add('page-loaded');

    // Inicjalizuj wszystkie funkcje
    initScrollReveal();
    initStickyHeader();
    initSmoothScroll();
    initScrollProgress();
    initParallaxElements();
    enhanceLazyLoad();

    console.log('GLAZ POL Animations initialized');
  }

  // Uruchom po załadowaniu DOM
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Reinicjalizacja dla Shopify Section Rendering API
  document.addEventListener('shopify:section:load', () => {
    initScrollReveal();
    enhanceLazyLoad();
  });

})();
